import { useApiData } from "../../contexts/ApiData";
import { useLocale, useTranslations } from "../../contexts/Translation";
import { Comment, ImprovementGoal } from "../../types";
import { useState } from "react";
import Modal from "../Modal/Modal";
import { H2 } from "../Headers/Headings";
import Button, { LinkButton } from "../Button/Button";
import { CoachButton } from "./CoachButton";
import { Coach, coachPortrait } from "../Comments/coach";
import styles from "./AiCoach.module.css";

type CoachingType = "surveyComment" | "goalComment" | "orgGoalComment" | "createGoal";

// The survey coaches answer together. On goals it is Lisa alone.
const coachesFor = (coachingType: CoachingType): Coach[] =>
  coachingType === "goalComment" || coachingType === "orgGoalComment" ? ["lisa"] : ["rhys", "lisa"];

type AiCoachResult = Comment | Comment[] | Partial<ImprovementGoal> | Record<string, never>;

type AiCoachProps = {
  coachingType: CoachingType;
  surveyId?: string;
  goalId?: string;
  orgGoalId?: string;
  onComplete: (result: AiCoachResult) => void;
  onError?: (error: string) => void;
  variant?: "onDark";
  buttonLabel?: string;
};

const getUrl = (coachingType: CoachingType, surveyId?: string, goalId?: string, orgGoalId?: string): string | null => {
  switch (coachingType) {
    case "surveyComment":
      return surveyId ? `/comment/survey/${surveyId}/ai-comment` : null;
    case "goalComment":
      return goalId ? `/comment/goal/${goalId}/ai-comment` : null;
    case "orgGoalComment":
      return orgGoalId ? `/comment/orggoal/${orgGoalId}/ai-comment` : null;
    case "createGoal":
      return surveyId ? `/goals/ai-goal?surveyId=${surveyId}` : null;
    default:
      return null;
  }
};

const AiCoach = ({ coachingType, surveyId, goalId, orgGoalId, onComplete, onError, variant, buttonLabel }: AiCoachProps) => {
  const t = useTranslations("aiCoach");
  const lang = useLocale();
  const { post, get } = useApiData();
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const coaches = coachesFor(coachingType);

  const handleGenerate = async () => {
    setLocalError(null);
    setShowConfirm(false);
    setIsGenerating(true);

    const url = getUrl(coachingType, surveyId, goalId, orgGoalId);

    if (!url) {
      setIsGenerating(false);
      const error = t("error");
      setLocalError(error);
      onError?.(error);
      return;
    }

    const result = coachingType === "createGoal" ? await get(url) : await post(url, { surveyId, goalId, lang });

    setIsGenerating(false);

    if (!result) {
      const error = t("error");
      setLocalError(error);
      onError?.(error);
      return;
    }

    onComplete(result);
  };

  if (isGenerating) {
    return (
      <div className={styles.pendingComment}>
        <p className={styles.pendingContent}>{t("loading")}</p>
        <p className={styles.pendingMeta}>
          {coaches.map((coach) => (
            <img key={coach} className={styles.pendingAiImage} src={coachPortrait(coach)} alt='' />
          ))}
          {t(coaches.length > 1 ? "writingBoth" : "writingLisa")}
        </p>
      </div>
    );
  }

  if (localError) {
    return (
      <div className={styles.errorComment}>
        <span>{localError}</span>
        <CoachButton
          coaches={coaches}
          variant={variant}
          onClick={() => {
            setLocalError(null);
            setShowConfirm(true);
          }}
        >
          {buttonLabel || t("buttonLabel")}
        </CoachButton>
      </div>
    );
  }

  return (
    <>
      <CoachButton coaches={coaches} variant={variant} onClick={() => setShowConfirm(true)}>
        {buttonLabel || t("buttonLabel")}
      </CoachButton>
      {showConfirm && (
        <Modal onClose={() => setShowConfirm(false)} title=''>
          <div className={styles.content}>
            <div className={styles.copy}>
              <H2>{t("heading")}</H2>
              <p>{t("info")}</p>
              <p>{t("dataSources")}</p>
              <p>{t("dataNotice")}</p>
              <p className={styles.aiDisclosure}>{t("aiDisclosure")}</p>
            </div>
            <div className={styles.portraits} data-testid='coach-modal-portraits'>
              {coaches.map((coach) => (
                <img key={coach} className={styles.image} src={coachPortrait(coach)} alt='' />
              ))}
            </div>
          </div>
          <div className={styles.actions}>
            <LinkButton onClick={() => setShowConfirm(false)}>{t("cancel")}</LinkButton>
            <Button onClick={handleGenerate}>{t("confirm")}</Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AiCoach;
